// File Metadata:
// Filename: EditCrossdock.js
// Version: 1.00
// Created: 2025-03-22 01:44:27
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGetCrossdocksQuery, useUpdateCrossdockMutation } from '../api/crossdockApiSlice'
import { Box, Card, CardHeader, CardContent, TextField, Button, Alert, List, ListItem, ListItemText, Typography, ImageList, ImageListItem, FormControl, InputLabel, Select, MenuItem } from '@mui/material'

const EditCrossdock = () => {
    const { id } = useParams()
    const navigate = useNavigate()

    const { crossdock } = useGetCrossdocksQuery('crossdocks', {
        selectFromResult: ({ data }) => ({
            crossdock: data?.entities[id]
        }),
    })

    const [updateCrossdock, { isLoading, isSuccess, error }] = useUpdateCrossdockMutation()

    const [location, setLocation] = useState('')
    const [deliveryDocuments, setDeliveryDocuments] = useState([])
    const [notes, setNotes] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [packageTypes, setPackageTypes] = useState({})
    const [departureTour, setDepartureTour] = useState('')

    useEffect(() => {
        if (isSuccess) {
            navigate('/dash/crossdock')
        }
    }, [isSuccess, navigate])

    useEffect(() => {
        if (crossdock) {
            setLocation(crossdock.location)
            setDeliveryDocuments(crossdock.deliveryDocuments || [])
            setNotes(crossdock.notes?.join('\n') || '')
            const newPackageTypes = {}
            crossdock.handlingUnits.forEach(hu => {
                newPackageTypes[hu.sscc] = hu.packageType
            })
            setPackageTypes(newPackageTypes)
            setDepartureTour(crossdock.departureTour || '')
        }
    }, [crossdock])

    const canSave = [location].every(Boolean) && !isLoading

    const handlePackageTypeChange = (sscc, value) => {
        setPackageTypes(prev => ({
            ...prev,
            [sscc]: value
        }))
    }

    const onSaveClicked = async () => {
        if (canSave) {
            try {
                const updatedHandlingUnits = crossdock.handlingUnits.map(hu => ({
                    ...hu,
                    packageType: packageTypes[hu.sscc]
                }))

                await updateCrossdock({
                    id,
                    location,
                    departureTour,
                    deliveryDocuments,
                    handlingUnits: updatedHandlingUnits,
                    notes: notes.split('\n').filter(note => note.trim())
                }).unwrap()
            } catch (err) {
                setErrMsg(err?.data?.message || 'Failed to save changes')
            }
        }
    }

    if (!crossdock) return <p>Loading...</p>

    return (
        <Box sx={{ maxWidth: 800, margin: '0 auto', p: 2 }}>
            <Card>
                <CardHeader 
                    title="Edit Crossdock Entry" 
                    subheader={`HUs: ${crossdock.handlingUnits.map(hu => hu.sscc).join(', ')}`}
                />
                <CardContent>
                    {errMsg && <Alert severity="error">{errMsg}</Alert>}
                    
                    <Typography variant="h6" gutterBottom>Arrival Information</Typography>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            margin="normal"
                            label="Arrival Tour"
                            value={crossdock.arrivalTour}
                            fullWidth
                            disabled
                        />
                        <TextField
                            margin="normal"
                            label="Location"
                            value={location}
                            onChange={e => setLocation(e.target.value)}
                            fullWidth
                            required
                        />
                    </Box>

                    <Typography variant="h6" gutterBottom>Departure Information</Typography>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            margin="normal"
                            label="Departure Tour"
                            value={departureTour}
                            onChange={e => setDepartureTour(e.target.value)}
                            fullWidth
                            helperText="Optional - Set this to assign HUs to a departure tour"
                        />
                    </Box>

                    <Typography variant="h6" gutterBottom>Handling Units</Typography>
                    <List sx={{ mb: 3 }}>
                        {crossdock.handlingUnits.map((hu, index) => (
                            <ListItem key={index}>
                                <ListItemText 
                                    primary={hu.sscc}
                                />
                                <FormControl sx={{ minWidth: 120 }}>
                                    <InputLabel>Package Type</InputLabel>
                                    <Select
                                        value={packageTypes[hu.sscc] || 'STILLAGE'}
                                        onChange={(e) => handlePackageTypeChange(hu.sscc, e.target.value)}
                                        label="Package Type"
                                    >
                                        <MenuItem value="STILLAGE">Stillage</MenuItem>
                                        <MenuItem value="BOX">Box</MenuItem>
                                        <MenuItem value="PALLET">Pallet</MenuItem>
                                    </Select>
                                </FormControl>
                            </ListItem>
                        ))}
                    </List>

                    <Typography variant="h6" gutterBottom>Documents & Notes</Typography>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            margin="normal"
                            label="Delivery Documents"
                            value={deliveryDocuments.join(', ')}
                            onChange={e => setDeliveryDocuments(e.target.value.split(',').map(doc => doc.trim()))}
                            fullWidth
                            helperText="Comma separated list of document numbers"
                        />
                        <TextField
                            margin="normal"
                            label="Notes"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                        />
                    </Box>

                    {crossdock.images?.length > 0 && (
                        <>
                            <Typography variant="h6" gutterBottom>Images</Typography>
                            <ImageList sx={{ width: '100%', height: 200 }} cols={3}>
                                {crossdock.images.map((img, index) => (
                                    <ImageListItem key={index}>
                                        <img
                                            src={`${img.url}?tr=w-200,h-200`}
                                            alt={`Crossdock ${index + 1}`}
                                            loading="lazy"
                                        />
                                    </ImageListItem>
                                ))}
                            </ImageList>
                        </>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={onSaveClicked}
                            disabled={!canSave}
                            fullWidth
                        >
                            Save Changes
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/dash/crossdock')}
                            fullWidth
                        >
                            Cancel
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}

export default EditCrossdock
